/**
 * Shared graph utilities — adjacency maps, edge filtering, viewport culling, label collision.
 */

import type { BBox, GraphLink, GraphNodeRenderable, NodeId, Point2D } from '../../types/graph';

/**
 * Build an adjacency map from links.
 * Returns Map<nodeId, Set<neighborId>>
 */
export function buildAdjacencyMap(links: GraphLink[]): Map<NodeId, Set<NodeId>> {
  const adj = new Map<NodeId, Set<NodeId>>();
  for (const link of links) {
    const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
    const targetId = typeof link.target === 'object' ? link.target.id : link.target;
    if (!adj.has(sourceId)) adj.set(sourceId, new Set());
    if (!adj.has(targetId)) adj.set(targetId, new Set());
    adj.get(sourceId)!.add(targetId);
    adj.get(targetId)!.add(sourceId);
  }
  return adj;
}

/**
 * Calculate edge weight percentile threshold.
 * Returns the minimum weight value so that `percentile` fraction of edges pass.
 */
export function getEdgePercentileThreshold(links: GraphLink[], percentile: number, weightKey = 'value'): number {
  if (!links.length || percentile >= 1) return 0;
  const weights = links.map(l => (l[weightKey] as number) || 0).sort((a, b) => a - b);
  const idx = Math.floor(weights.length * (1 - percentile));
  return weights[Math.max(0, idx)];
}

/**
 * Check if a node is within the visible viewport (with padding).
 */
export function isInViewport(node: GraphNodeRenderable, bbox: BBox, padding = 50): boolean {
  if (node.x == null || node.y == null) return false;
  return (
    node.x >= bbox.left - padding &&
    node.x <= bbox.right + padding &&
    node.y >= bbox.top - padding &&
    node.y <= bbox.bottom + padding
  );
}

/**
 * Simple spatial hash grid for label collision avoidance.
 * Processes nodes sorted by degree (most connected first).
 * Returns Set<nodeId> of nodes that should show labels, or null to show all.
 */
export function computeVisibleLabels(
  nodes: GraphNodeRenderable[],
  adjacencyMap: Map<NodeId, Set<NodeId>>,
  globalScale: number,
  bbox: BBox,
  maxLabels = 120
): Set<NodeId> | null {
  // At detail zoom, show all
  if (globalScale > 4.0) return null;

  const cellSize = 80 / globalScale;
  const grid = new Map<string, boolean>();
  const visible = new Set<NodeId>();

  // Filter to viewport and sort by degree
  const candidates = nodes
    .filter(n => n.x != null && n.y != null && isInViewport(n, bbox, 60))
    .sort((a, b) => {
      const degA = adjacencyMap.get(a.id)?.size || 0;
      const degB = adjacencyMap.get(b.id)?.size || 0;
      return degB - degA;
    });

  for (const node of candidates) {
    if (visible.size >= maxLabels) break;
    const cellX = Math.floor(node.x! / cellSize);
    const cellY = Math.floor(node.y! / cellSize);
    const key = `${cellX},${cellY}`;
    if (!grid.has(key)) {
      grid.set(key, true);
      visible.add(node.id);
    }
  }

  return visible;
}

/**
 * Get the viewport bounding box from a react-force-graph-2d ref.
 */
export function getViewportBBox(graphRef: React.RefObject<any>): BBox | null {
  if (!graphRef.current) return null;
  const fg = graphRef.current;
  try {
    // Get canvas dimensions
    const canvasEl = fg.canvas?.() || document.querySelector('canvas');
    const width = canvasEl?.width || 800;
    const height = canvasEl?.height || 600;
    const topLeft = fg.screen2GraphCoords(0, 0);
    const bottomRight = fg.screen2GraphCoords(width, height);
    return {
      left: topLeft.x,
      top: topLeft.y,
      right: bottomRight.x,
      bottom: bottomRight.y,
    };
  } catch {
    return { left: -Infinity, top: -Infinity, right: Infinity, bottom: Infinity };
  }
}

/**
 * Truncate a label string with ellipsis.
 */
export function truncateLabel(text: string, maxLength = 20): string {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength - 1) + '\u2026';
}

/**
 * Identify key collaborators by degree centrality.
 * Returns Set of node IDs with degree >= threshold within the visible graph.
 */
export function findKeyCollaborators(adjacencyMap: Map<NodeId, Set<NodeId>>, minDegree = 5): Set<NodeId> {
  const keys = new Set<NodeId>();
  adjacencyMap.forEach((neighbors, nodeId) => {
    if (neighbors.size >= minDegree) keys.add(nodeId);
  });
  return keys;
}

/**
 * Label propagation community detection.
 * Returns Map<nodeId, communityIndex> (0-based integer communities).
 */
export function detectCommunities(nodes: GraphNodeRenderable[], links: GraphLink[]): Map<NodeId, number> {
  if (!nodes.length) return new Map();

  const community = new Map<NodeId, number>();
  nodes.forEach((n, i) => community.set(n.id, i));

  const adj = new Map<NodeId, NodeId[]>();
  const weights = new Map<string, number>();
  for (const link of links) {
    const s = typeof link.source === 'object' ? link.source.id : link.source;
    const t = typeof link.target === 'object' ? link.target.id : link.target;
    const w = link.value || link.sharedProjects || 1;
    if (!adj.has(s)) adj.set(s, []);
    if (!adj.has(t)) adj.set(t, []);
    adj.get(s)!.push(t);
    adj.get(t)!.push(s);
    weights.set(`${s}-${t}`, w);
    weights.set(`${t}-${s}`, w);
  }

  for (let iter = 0; iter < 15; iter++) {
    let changed = false;
    const shuffled = [...nodes].sort(() => Math.random() - 0.5);
    for (const node of shuffled) {
      const neighbors = adj.get(node.id);
      if (!neighbors || neighbors.length === 0) continue;
      const commWeights: Record<number, number> = {};
      for (const nid of neighbors) {
        const c = community.get(nid);
        if (c == null) continue;
        const w = weights.get(`${node.id}-${nid}`) || 1;
        commWeights[c] = (commWeights[c] || 0) + w;
      }
      let bestComm = community.get(node.id)!;
      let bestWeight = -1;
      for (const [c, w] of Object.entries(commWeights)) {
        if (w > bestWeight) { bestWeight = w; bestComm = Number(c); }
      }
      if (bestComm !== community.get(node.id)) {
        community.set(node.id, bestComm);
        changed = true;
      }
    }
    if (!changed) break;
  }

  // Remap community IDs to 0-based contiguous indices
  const uniqueComms = Array.from(new Set(Array.from(community.values())));
  const remap = new Map<number, number>();
  uniqueComms.forEach((c, i) => remap.set(c, i));
  const result = new Map<NodeId, number>();
  community.forEach((c, nodeId) => {
    result.set(nodeId, remap.get(c)!);
  });
  return result;
}

/**
 * Compute convex hull for a set of 2D points using Graham scan.
 * Returns array of {x, y} in counter-clockwise order.
 */
export function convexHull(points: Point2D[]): Point2D[] {
  if (points.length < 3) return points;
  const sorted = [...points].sort((a, b) => a.x - b.x || a.y - b.y);

  const cross = (O: Point2D, A: Point2D, B: Point2D) => (A.x - O.x) * (B.y - O.y) - (A.y - O.y) * (B.x - O.x);
  const lower: Point2D[] = [];
  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0)
      lower.pop();
    lower.push(p);
  }
  const upper: Point2D[] = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i];
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0)
      upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}
